import React from 'react';
import { InfoBox, ExerciseBox, CodeBlock, InlineCode, SectionTitle, Note, Image, TableContainer, Highlight, PatternContainer, SectionHeading, SubHeading, TwoColGrid, ThreeColGrid, GridItem, Divider, Separator, Result, CorrectionBox } from '../../../../components/course/CourseComponents';
import screen1 from '../repere2.jpg';

const Chapitre5Content = () => {
  return (
    <>
      <InfoBox title="Objectif du chapitre" color="indigo">
        <p className="mb-0">
          Détection des collisions avec les murs et soi-même
        </p>
      </InfoBox>

      <Separator type="text" text="Détection des collisions avec les murs" color="gray-darker" size="md" />
      <SectionTitle>🧱 1/ Détection des collisions avec les murs</SectionTitle>

      <p>Le joueur perd si le serpent touche le bord de l'écran.</p>
      <p>Il faut donc détecter quand la tête touche les limites du canvas.</p>

      <p>Pour rappel, la tête fait 40x40px et ses coordonnées sont définies par <InlineCode>posX</InlineCode> et <InlineCode>posY</InlineCode>.
      <br/>On utilisera la variable <InlineCode>rayon</InlineCode>, comme pour la pomme, qui vaut 20. Le point d'origine de la tête est au centre de l'image.</p>

      <p><strong>Tests de collision :</strong></p>

      <ul className="list-disc list-inside space-y-2 ml-4">
        <li><strong>Test du bord gauche :</strong> si <InlineCode>posX - 20 &lt; 0</InlineCode>, alors la tête touche le bord gauche</li>
        <li><strong>Test du bord droit :</strong> si <InlineCode>posX + 20 &gt; largeur de l'écran</InlineCode>, alors on touche le bord droit</li>
        <li><strong>Test du bord haut :</strong> si <InlineCode>posY - 20 &lt; 0</InlineCode>, alors on touche le bord haut</li>
        <li><strong>Test du bord bas :</strong> si <InlineCode>posY + 20 &gt; hauteur de l'écran</InlineCode>, on touche le bord bas</li>
      </ul>

      <p>On peut coder toutes ces conditions en une seule, en ajoutant le <InlineCode>||</InlineCode> (OU), car une seule condition suffit pour perdre la partie :</p>

      <CodeBlock language="javascript">
{`if (posX - rayon < 0 || posX + rayon > width || posY - rayon < 0 || posY + rayon > height) {
  gameOver("AIE, LE MUR !");
}`}
      </CodeBlock>

      <Separator type="text" text="Écran de fin" color="gray-darker" size="md" />
      <SectionTitle>💀 2/ Écran de fin</SectionTitle>

      <p>S'il y a collision, on appelle la fonction <InlineCode>gameOver("texte")</InlineCode> qui va afficher un message de fin et arrêter la boucle du jeu.</p>

      <p>Voici la fonction <InlineCode>gameOver</InlineCode> commentée ligne par ligne :</p>

      <CodeBlock language="javascript">
{`function gameOver(msg) {
  noLoop(); // Arrête le jeu
  image(imageTeteMort, posX, posY); // Affiche la tête morte du serpent
  fill(255); // En blanc
  textAlign(CENTER, TOP); // En haut, au milieu
  textStyle(BOLD); // En gras
  textSize(32); // Taille 32
  text(msg, width / 2, 70); // Affiche le message envoyé en paramètre "msg"
}`}
      </CodeBlock>

      <Note type="exercice" title="🧪 À toi de jouer : détection des murs et écran de fin">
        <p>- À la fin de la fonction <InlineCode>draw</InlineCode>, détecte si la tête du serpent (<InlineCode>posX</InlineCode>, <InlineCode>posY</InlineCode>) touche les 4 murs.
        <br/>Si oui, appelle la fonction <InlineCode>gameOver("AIE, LE MUR !");</InlineCode></p>

        <CodeBlock language="javascript">
{`if (posX - rayon < 0 || posX + rayon > width || posY - rayon < 0 || posY + rayon > height) {
  gameOver("AIE, LE MUR !");
}`}
        </CodeBlock>

        <p>- En bas du fichier, crée la fonction <InlineCode>gameOver(msg)</InlineCode> qui arrête le jeu et affiche le message de fin :</p>

        <CodeBlock language="javascript">
{`//--------------------------------- ÉCRAN FIN ------------------------------------------
function gameOver(msg) {
  noLoop();
  image(imageTeteMort, posX, posY);
  fill(255);
  textAlign(CENTER, TOP);
  textStyle(BOLD);
  textSize(32);
  text(msg, width / 2, 70);
}`}
        </CodeBlock>

        <br/>
        <Result>Si le serpent touche les bords de l'écran, le jeu s'arrête et un message de fin apparaît.</Result>
      </Note>

      <Separator type="text" text="Collision avec soi-même" color="gray-darker" size="md" />
      <SectionTitle>🐍 3/ Collision avec soi-même</SectionTitle>

      <p>Le joueur perd également s'il touche son propre corps.</p>

      <p>Comme on l'a fait pour replacer la nourriture loin du corps, on parcourt tous les éléments du corps, et on regarde si la distance entre la tête et le corps est inférieure à 40.</p>
      <p>Si c'est le cas, cela veut dire que la tête touche un élément du corps.</p>

      <p>Par souci de performance, on parcourt la liste de 8 en 8, car ce n'est pas la peine de tester chaque pixel du corps du serpent.</p>

      <p>Pour calculer la distance, on utilise la fonction p5.js <InlineCode>dist</InlineCode> :</p>
      <TableContainer>
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-800">
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Fonction</th>
              <th className="border border-gray-300 dark:border-gray-600 p-2 text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 dark:border-gray-600 p-2"><InlineCode>dist(x1, y1, x2, y2)</InlineCode></td>
              <td className="border border-gray-300 dark:border-gray-600 p-2">Calcule la distance entre deux points. <br/>- Paramètre 1 : position x du premier point<br/> - Paramètre 2 : position y du premier point<br/> - Paramètre 3 : position x du deuxième point<br/> - Paramètre 4 : position y du deuxième point<br/> - Retourne : la distance en pixels</td>
            </tr>
          </tbody>
        </table>
      </TableContainer>

      <p>La tête a pour coordonnées <InlineCode>posX</InlineCode>, <InlineCode>posY</InlineCode>, et chaque élément du corps a une variable <InlineCode>x</InlineCode> et <InlineCode>y</InlineCode> :</p>

      <CodeBlock language="javascript">
{`// Collision soi-même
for (let i = 0; i < corps.length - 15; i += 8) {
  if (dist(posX, posY, corps[i].x, corps[i].y) < rayon * 2) {
    gameOver("TU T'ES MORDU !");
  }
}`}
      </CodeBlock>

      <Note type="tip" title="💡 TIPS : pourquoi on teste toute la liste corps en oubliant les 15 derniers ?">
        <p>Dans le for, i s'arrete 15 points avant la fin de la liste : <InlineCode>i &lt; corps.length - 15</InlineCode></p>
        <p>Les 15 derniers éléments sont ceux proches de la tête. Certains éléments du corps se trouvent sous la tête.
        <br/>Si on les teste, alors le joueur perdra instantanément.
        <br/>Il faut donc sauter les éléments du corps situés juste derrière la tête.</p>
      </Note>

      <Note type="exercice" title="🧪 À toi de jouer : détection des collisions avec soi-même">
        <p>- À la fin de la fonction <InlineCode>draw</InlineCode>, parcours la liste <InlineCode>corps</InlineCode> de 8 en 8, en omettant les 15 derniers.</p>
        <p>- Puis calcule la distance entre <InlineCode>posX</InlineCode>, <InlineCode>posY</InlineCode> et <InlineCode>corps[i].x</InlineCode>, <InlineCode>corps[i].y</InlineCode></p>
        <p>- Si la distance est inférieure à 40, alors appelle la fonction <InlineCode>gameOver("TU T'ES MORDU !");</InlineCode></p>

        <CodeBlock language="javascript">
{`// Collision soi-même
for (let i = 0; i < corps.length - 15; i += 8) {
  if (dist(posX, posY, corps[i].x, corps[i].y) < rayon * 2) {
    gameOver("TU T'ES MORDU !");
  }
}`}
        </CodeBlock>

        <br/>
        <Result>Si le serpent se mord la queue, le jeu s'arrête et l'écran de fin apparaît.</Result>
      </Note>

      <Separator type="text" text="Code complet" color="gray-darker" size="md" />
      <SectionTitle>✅ 4/ Code complet : Collision avec les murs et soi-même</SectionTitle>


      <CodeBlock language="javascript">
{`// Jeu 4 : Snake
let imageFond;
let imageTete1;
let imageTete2;
let imageTeteMort;
let imageCorps;
let imagePomme;
let sonMort;
let sonCroque

let vitesse = 270; // vitesse du snake en pixels par seconde
let longueur = 16; // longueur initiale du snake
let nourriture = 12; // longueur supplémentaire à chaque pomme

let posX, posY;
let dx = 1, dy = 0;
let corps = [];
let nourritureX, nourritureY;
let rayon = 20;

//--------------------------------- PRELOAD ------------------------------------------
function preload(){
  imageFond = loadImage('fond.png')
  imageTete1 = loadImage('snake-tete1.png');
  imageTete2 = loadImage('snake-tete2.png');
  imageTeteMort = loadImage('snake-tete-mort.png');
  imageCorps = loadImage('snake-corps.png');
  imagePomme = loadImage('pomme.png');
  sonMort = loadSound('mort.wav')
  sonCroque = loadSound('croque.wav')
}

//--------------------------------- SETUP ------------------------------------------
function setup() {
  createCanvas(500, 500);
  posX = 40;
  posY = height / 2;
  placerNourriture();
}

//--------------------------------- DRAW ------------------------------------------
function draw() {
  imageMode(CORNER);
  background(imageFond);
  imageMode(CENTER);

  // Déplacement
  let dt = deltaTime / 1000;
  posX = posX + dx * vitesse * dt;
  posY = posY + dy * vitesse * dt;
  corps.push({ x: posX, y: posY });

  // Taille limite
  while (corps.length > longueur) {
    corps.shift();
  }

  // Affichage corps
  for (let i = 0; i < corps.length; i = i + 4) {
    let p = corps[i];
    image(imageCorps, p.x, p.y);
  }

  // Affichage et animation de la tête
  let imageTete;
  if (frameCount % 100 > 10)  imageTete = imageTete1;
  else imageTete = imageTete2;
  image(imageTete, posX, posY);

  // Affichage nourriture
  image(imagePomme, nourritureX, nourritureY);

  // Collision nourriture
  let distanceNourriture = dist(posX, posY, nourritureX, nourritureY);
  if (distanceNourriture < rayon * 2) {
    longueur = longueur + nourriture;
    placerNourriture();
  }

  // Collision mur
  if (posX - rayon < 0 || posX + rayon > width || posY-rayon < 0 || posY+rayon > height) {
    gameOver("AIE, LE MUR !");
  }

  // Collision soi-même
  for (let i = 0; i < corps.length - 15; i += 8) {
    if (dist(posX, posY, corps[i].x, corps[i].y) < rayon*2) {
      gameOver("TU T'ES MORDU !");
    }
  }
}

//--------------------------------- CLAVIER ------------------------------------------
function keyPressed() {
  if (keyCode === UP_ARROW && dy !== 1) { dx = 0; dy = -1; }
  else if (keyCode === DOWN_ARROW && dy !== -1) { dx = 0; dy = 1; }
  else if (keyCode === LEFT_ARROW && dx !== 1) { dx = -1; dy = 0; }
  else if (keyCode === RIGHT_ARROW && dx !== -1) { dx = 1; dy = 0; }
}

//--------------------------------- NOURRITURE ------------------------------------------
function placerNourriture() {
  let essais = 0;
  while (essais < 100) {
    let x = random(rayon, width - rayon);
    let y = random(rayon, height - rayon);
    let positionValide = true;
    for (let i = 0; i < corps.length; i += 8) {
      if (dist(x, y, corps[i].x, corps[i].y) < rayon * 2) {
        positionValide = false;
        break;
      }
    }
    if (positionValide) {
      nourritureX = x;
      nourritureY = y;
      return;
    }
    essais++;
  }

  // Si on dépasse 100 essais, on place quand même la nourriture quelque part
  nourritureX = random(rayon, width - rayon);
  nourritureY = random(rayon, height - rayon);
}

//--------------------------------- ECRAN FIN ------------------------------------------
function gameOver(msg) {
  noLoop();
  image(imageTeteMort, posX, posY);
  fill(255);
  textAlign(CENTER, TOP);
  textStyle(BOLD);
  textSize(32);
  text(msg, width / 2, 70);
}`}
      </CodeBlock>


      <p>🎯 Dans le prochain chapitre, nous ajouterons du son et un score !</p>
    </>
  );
};

export default Chapitre5Content; 

